import { useCallback, useEffect, useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_SELLER_TRANSACTIONS } from 'api/graphql/queries/transactions/getTransactionsList';
import { GET_SELLER_TRANSACTION_TOTALS } from 'api/graphql/queries/transactions/getTransactionsTotals';
import { PaginationState } from '@tanstack/react-table';

// Format date as "MMM d, yyyy, h:mm a" - e.g., "Dec 12, 2025, 12:56 PM"
const formatTransactionDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    
    // Get month abbreviation
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    
    // Get day and year
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Get time in 12-hour format with AM/PM
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString; // Return original string if formatting fails
  }
};

// Define Transaction type based on the GraphQL schema
export interface Transaction {
  transaction_id: string;
  bank_transaction_id: string;
  entity_transaction_id: string;
  message: string;
  date_of_transaction: string;
  formatted_date: string;
  transaction_amount: number;
  total_amount: number;
  comission_amount: number;  // Field spelled as expected by the UI components
  type: string;
}

const transformTransactions = (transactions: any[]): Transaction[] => {
  return transactions.map((item) => {
    // Transform the API response to match the required Transaction format
    return {
      transaction_id: item.transaction_id || '',
      bank_transaction_id: item.bank_transaction_id || '',
      entity_transaction_id: item.entity_transaction_id || '',
      message: item.message || '',
      date_of_transaction: item.date_of_transaction || '',
      formatted_date: formatTransactionDate(item.date_of_transaction),
      transaction_amount: item.transaction_amount || 0,
      total_amount: item.sales_data?.total_amount || 0,
      comission_amount: item.sales_data?.total_commission || 0,
      type: item.type || '',
    };
  });
};

interface TransactionsDataOptions {
  initialPageIndex?: number;
  initialPageSize?: number;
}

interface FilterOptions {
  search?: string;
  fromDate?: string;
  toDate?: string;
  type?: string;
}

interface SortOptions {
  transactionId?: number;
  transactionDate?: number;
  totalAmount?: number;
}

export interface TransactionsDataResult {
  tableData: Transaction[];
  loading: boolean;
  error: any;
  paginationCount: {
    totalPages: number;
    totalCount: number;
  };
  pagination: PaginationState;
  handlePageChange: (newPageIndex: number) => void;
  handlePageSizeChange: (newPagination: PaginationState) => void;
  fetchTransactions: () => void;
  orderTotals: {
    total_amount: number;
    total_sales: number;
    total_commission: number;
    total_earnings: number;
    total_payment: number;
    total_withdrawal: number;
  } | null;
}

export function useTransactionsData(
  filterOptions: FilterOptions = {},
  sortOptions: SortOptions = {},
  options: TransactionsDataOptions = {}
): TransactionsDataResult {
  // State for table data
  const [tableData, setTableData] = useState<Transaction[]>([]);
  
  // State for pagination
  const [paginationCount, setPaginationCount] = useState({
    totalPages: 0,
    totalCount: 0
  });
  
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: options.initialPageIndex || 0,
    pageSize: options.initialPageSize || 10,
  });

  // State for order totals
  const [orderTotals, setOrderTotals] = useState<{
    total_amount: number;
    total_sales: number;
    total_commission: number;
    total_earnings: number;
    total_payment: number;
    total_withdrawal: number;
  } | null>(null);
  
  // Cache the totals query to avoid unnecessary refetching
  const totalsQueryCompletedRef = useRef(false);
  
  // Function to build active filters for API calls
  const getActiveFilters = useCallback(() => {
    const filters: any = {};
    
    if (filterOptions.search) {
      filters.search = filterOptions.search; // Single search parameter that will match across transaction or bank IDs
    }
    
    if (filterOptions.fromDate) {
      filters.from_date = filterOptions.fromDate;
    }
    
    if (filterOptions.toDate) {
      filters.to_date = filterOptions.toDate;
    }
    
    if (filterOptions.type) {
      filters.type = filterOptions.type;
    }
    
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterOptions]);
  
  // Query for fetching transactions
  const [fetchTransactionsQuery, { loading: transactionsLoading, error: transactionsError, data: transactionsData }] = useLazyQuery(GET_SELLER_TRANSACTIONS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  
  // Separate query for fetching order totals
  const [fetchTotalsQuery, { loading: totalsLoading, error: totalsError, data: totalsData }] = useLazyQuery(GET_SELLER_TRANSACTION_TOTALS, {
    fetchPolicy: "cache-and-network", // Use cache if available but still check network
    notifyOnNetworkStatusChange: true,
  });
  
  // Stable references to current filter values to avoid infinite loops
  const currentFilters = useCallback(getActiveFilters, [getActiveFilters]);
  
  // Function to fetch transactions with current pagination and filter options
  const fetchTransactions = useCallback((pageIndex?: number, pageSize?: number) => {
    // Use provided pagination params or fall back to state values
    const currentPage = (pageIndex !== undefined ? pageIndex : pagination.pageIndex) + 1;
    const currentPageSize = pageSize || pagination.pageSize;
    
    fetchTransactionsQuery({
      variables: {
        currentPage: currentPage,
        pageSize: currentPageSize,
        filter: currentFilters(),
        skipTotals: true // Tell the server not to calculate totals with this query
      },
    });
  }, [fetchTransactionsQuery, pagination.pageIndex, pagination.pageSize, currentFilters]);
  
  // Separate function to fetch just the order totals
  const fetchOrderTotals = useCallback(() => {
    if (!totalsQueryCompletedRef.current || !orderTotals) {
      console.log("Fetching order totals (first time or after filter change)");
      fetchTotalsQuery();
    } else {
      console.log("Skipping totals fetch - already have data");
    }
  }, [fetchTotalsQuery, orderTotals]);
  
  // Initial fetch on component mount using a ref to ensure it runs only once
  const initialFetchRef = useRef(false);
  useEffect(() => {
    if (!initialFetchRef.current) {
      fetchTransactions();
      fetchOrderTotals(); // Fetch totals separately
      initialFetchRef.current = true;
    }
  }, [fetchTransactions, fetchOrderTotals]);
  
  // Pagination handlers
  const handlePageChange = (newPageIndex: number) => {
    // Calculate the maximum valid page index
    const maxPageIndex = Math.max(0, paginationCount.totalPages - 1);
    const safePageIndex = Math.min(newPageIndex, maxPageIndex);
    
    // Update local pagination state
    setPagination(prev => ({
      ...prev,
      pageIndex: safePageIndex
    }));
    
    // Fetch data for the new page (only transaction data, not totals)
    fetchTransactions(safePageIndex, pagination.pageSize);
  };
  
  // Handle page size changes
  const handlePageSizeChange = (newPagination: PaginationState) => {
    // Update local pagination state
    setPagination(newPagination);
    
    // Always reset to first page when changing page size
    fetchTransactions(0, newPagination.pageSize);
  };
  
  // Update the table data when API response is received
  useEffect(() => {
    if (transactionsData?.sellerTransactions) {
      // Update pagination counts regardless of items presence
      setPaginationCount({
        totalCount: transactionsData.sellerTransactions.total_count || 0,
        totalPages: Math.max(1, transactionsData.sellerTransactions.page_info?.total_pages || 1),
      });
      
      // Process transaction items if they exist
      if (transactionsData.sellerTransactions.items && transactionsData.sellerTransactions.items.length > 0) {
        const transformed = transformTransactions(transactionsData.sellerTransactions.items);
        setTableData(transformed);
      } else {
        setTableData([]);
      }
    }
  }, [transactionsData]);
  
  // Update order totals when that query returns
  useEffect(() => {
    if (totalsData?.sellerTransactions?.order_totals) {
      setOrderTotals(totalsData.sellerTransactions.order_totals);
      totalsQueryCompletedRef.current = true; // Mark that we've successfully fetched totals
    }
  }, [totalsData]);
  
  // Use refs to track previous filter values to avoid unnecessary rerenders
  const prevFilterOptionsRef = useRef('');
  
  // Refetch data when filter options change
  useEffect(() => {
    const currentFilterOptionsString = JSON.stringify(filterOptions);
    
    // Only update if the values have actually changed
    if (currentFilterOptionsString !== prevFilterOptionsRef.current) {
      setPagination((prev) => ({ pageIndex: 0, pageSize: prev.pageSize }));
      
      // Reset the totals query completed flag to force a refresh
      totalsQueryCompletedRef.current = false;
      
      // Fetch transactions with new filters
      fetchTransactionsQuery({
        variables: {
          currentPage: 1,
          pageSize: pagination.pageSize,
          filter: currentFilters(),
          skipTotals: true
        },
      });
      
      // Also refresh the totals when filters change
      fetchOrderTotals();
      
      // Update refs with current values
      prevFilterOptionsRef.current = currentFilterOptionsString;
    }
  }, [filterOptions, pagination.pageSize, fetchTransactionsQuery, currentFilters, fetchOrderTotals]);

  // Only show loading for table data during pagination
  // Use combined loading state for initial load
  const loading = orderTotals ? transactionsLoading : (transactionsLoading || totalsLoading);
  
  // Combined error state - prioritize transaction errors
  const error = transactionsError || totalsError;

  return {
    tableData,
    loading, // Only use transactions loading after initial load
    error,
    paginationCount,
    pagination,
    handlePageChange,
    handlePageSizeChange,
    fetchTransactions,
    orderTotals,
  };
}
